import { useState, useEffect } from 'react';
import { useParams, Link, useSearchParams, useNavigate } from 'react-router-dom';
import { KeyRound, Plug, ShieldOff, Key, Network } from 'lucide-react';
import { useClient, useSetCredential, useDeactivateCredential, useUpdateClient } from '../hooks/useClients.js';
import { apiGet } from '../api/client.js';
import Modal from '../components/Modal.jsx';
import Button from '../components/Button.jsx';
import Input from '../components/Input.jsx';
import Badge from '../components/Badge.jsx';
import Spinner from '../components/Spinner.jsx';

const statusVariant  = { active: 'success', suspended: 'warning', disabled: 'danger' };
const statusLabel    = { active: 'Activo', suspended: 'Suspendido', disabled: 'Deshabilitado' };
const STATUS_OPTIONS = ['active', 'suspended', 'disabled'];

export default function ClientDetail() {
  const { id } = useParams();
  const { data: client, isLoading, refetch } = useClient(id);
  const setCredMutation = useSetCredential(id);
  const deactivateMutation = useDeactivateCredential(id);
  const updateMutation = useUpdateClient(id);
  const [credOpen, setCredOpen] = useState(false);
  const [deactivateConfirm, setDeactivateConfirm] = useState(false);
  const [cred, setCred] = useState({ accessToken: '', authType: 'static' });
  const [credMsg, setCredMsg] = useState(null);
  const [oauthLoading, setOauthLoading] = useState(false);
  const [oauthToast, setOauthToast] = useState(null); // { type: 'success'|'error', text }

  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  // Detectar retorno del flujo OAuth
  useEffect(() => {
    const status = searchParams.get('oauth');
    const msg    = searchParams.get('msg');
    if (status === 'success') {
      setOauthToast({ type: 'success', text: '✓ Cuenta de MercadoPago conectada correctamente via OAuth' });
      refetch();
      navigate(`/clients/${id}`, { replace: true });
    } else if (status === 'error') {
      setOauthToast({ type: 'error', text: `✗ ${msg ? decodeURIComponent(msg) : 'Error en la autorización'}` });
      navigate(`/clients/${id}`, { replace: true });
    }
  }, [searchParams, id, navigate, refetch]);

  if (isLoading) return <div className="flex justify-center py-10"><Spinner size="lg" /></div>;
  if (!client)  return <div className="card text-slate-400">Cliente no encontrado</div>;

  const handleSaveCred = () => {
    setCredMsg(null);
    setCredMutation.mutate(cred, {
      onSuccess: () => { setCredOpen(false); setCredMsg('Credencial guardada correctamente'); },
      onError:   (e) => setCredMsg(e.response?.data?.message || 'Error al guardar la credencial'),
    });
  };

  const handleStatusChange = (newStatus) => {
    if (newStatus === client.status) return;
    updateMutation.mutate({ status: newStatus }, {
      onError: (e) => setOauthToast({ type: 'error', text: e.response?.data?.message || 'Error al cambiar estado' }),
    });
  };

  const handleDeactivate = () => {
    deactivateMutation.mutate(undefined, {
      onSuccess: () => { setDeactivateConfirm(false); refetch(); },
      onError: (e) => setOauthToast({ type: 'error', text: e.response?.data?.message || 'Error al desactivar' }),
    });
  };

  const handleConnectOauth = async () => {
    setOauthLoading(true);
    setOauthToast(null);
    try {
      const { url } = await apiGet(`/oauth/start?clientId=${id}`);
      // Abrir en la misma pestaña — el callback redirige de vuelta
      window.location.href = url;
    } catch (e) {
      setOauthToast({ type: 'error', text: e.response?.data?.error || 'No se pudo iniciar la autorización' });
      setOauthLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <Link to="/clients" className="text-slate-400 hover:text-white text-sm">← Clientes</Link>
      </div>

      {oauthToast && (
        <div className={`p-3 rounded-lg border text-sm flex items-center justify-between ${
          oauthToast.type === 'success' ? 'alert-success' : 'alert-error'
        }`}>
          <span>{oauthToast.text}</span>
          <button onClick={() => setOauthToast(null)} className="ml-4 opacity-60 hover:opacity-100 text-lg leading-none">✕</button>
        </div>
      )}

      <div className="card space-y-3">
        <div className="flex items-start justify-between gap-4">
          <h2 className="text-xl font-semibold">{client.business_name}</h2>
          <div className="flex items-center gap-2 shrink-0">
            <span className="text-xs text-slate-400">Estado:</span>
            <div className="flex gap-1">
              {STATUS_OPTIONS.map(s => (
                <button
                  key={s}
                  onClick={() => handleStatusChange(s)}
                  disabled={updateMutation.isPending}
                  className={`px-2.5 py-0.5 rounded-full text-xs font-medium border transition-all ${
                    client.status === s
                      ? s === 'active'   ? 'bg-emerald-500/20 border-emerald-500 text-emerald-400'
                      : s === 'suspended' ? 'bg-amber-500/20 border-amber-500 text-amber-400'
                      :                    'bg-red-500/20 border-red-500 text-red-400'
                      : 'bg-transparent border-slate-600 text-slate-500 hover:border-slate-400 hover:text-slate-300'
                  }`}
                >
                  {statusLabel[s]}
                </button>
              ))}
            </div>
          </div>
        </div>
        <dl className="grid grid-cols-2 gap-3 text-sm">
          <div><dt className="text-slate-400">Código</dt><dd className="font-mono">{client.code}</dd></div>
          <div><dt className="text-slate-400">MP User ID</dt><dd className="font-mono">{client.mp_user_id || '—'}</dd></div>
          <div><dt className="text-slate-400">CUIT</dt><dd>{client.tax_id || '—'}</dd></div>
          <div><dt className="text-slate-400">Email</dt><dd>{client.email || '—'}</dd></div>
          {client.description && (
            <div className="col-span-2"><dt className="text-slate-400">Descripción</dt><dd>{client.description}</dd></div>
          )}
        </dl>
      </div>

      <div className="flex gap-3 flex-wrap items-center">
        <Button onClick={() => setCredOpen(true)} variant="secondary">
          <KeyRound size={14} className="mr-1.5 shrink-0" /> Credencial manual
        </Button>
        <Button onClick={handleConnectOauth} loading={oauthLoading}>
          <Plug size={14} className="mr-1.5 shrink-0" /> Conectar OAuth
        </Button>
        <Button variant="danger" onClick={() => setDeactivateConfirm(true)}>
          <ShieldOff size={14} className="mr-1.5 shrink-0" /> Desactivar credencial
        </Button>
        <Link to={`/clients/${id}/api-keys`}>
          <Button variant="secondary"><Key size={14} className="mr-1.5 shrink-0" /> API Keys</Button>
        </Link>
        <Link to={`/clients/${id}/mp-map`}>
          <Button variant="secondary"><Network size={14} className="mr-1.5 shrink-0" /> Mapa MP</Button>
        </Link>
      </div>

      {/* Modal confirmación desactivar credencial */}
      <Modal
        open={deactivateConfirm}
        onClose={() => setDeactivateConfirm(false)}
        title="Desactivar credencial"
        footer={
          <>
            <Button variant="ghost" onClick={() => setDeactivateConfirm(false)}>Cancelar</Button>
            <Button variant="danger" loading={deactivateMutation.isPending} onClick={handleDeactivate}>
              Desactivar
            </Button>
          </>
        }
      >
        <p className="text-sm text-slate-300">
          La credencial activa de <strong>{client.business_name}</strong> quedará desactivada.
          El cliente no podrá procesar pagos hasta que se configure una nueva credencial o se realice el flujo OAuth.
        </p>
      </Modal>

      <div className="card text-xs text-slate-500 space-y-1">
        <p><span className="text-slate-300 font-medium">Credencial manual:</span> ingresás el access_token directamente. Útil para testing o credenciales de producción ya obtenidas.</p>
        <p><span className="text-slate-300 font-medium">Conectar OAuth:</span> el comercio autoriza el acceso desde su cuenta de MP. Obtenés access_token + refresh_token automáticamente. El refresh_token se renueva cada 6 meses via n8n.</p>
      </div>

      {credMsg && <p className="text-sm text-emerald-400">{credMsg}</p>}

      <Modal open={credOpen} onClose={() => setCredOpen(false)} title="Credencial MercadoPago (manual)"
        footer={
          <><Button variant="ghost" onClick={() => setCredOpen(false)}>Cancelar</Button>
          <Button loading={setCredMutation.isPending} onClick={handleSaveCred}>Guardar</Button></>
        }
      >
        <Input label="Access Token *" type="password" value={cred.accessToken}
          onChange={(e) => setCred(c => ({ ...c, accessToken: e.target.value }))}
          placeholder="APP_USR-..." />
        <div>
          <label className="label">Tipo</label>
          <select className="input-base" value={cred.authType}
            onChange={(e) => setCred(c => ({ ...c, authType: e.target.value }))}>
            <option value="static">Static</option>
            <option value="oauth">OAuth</option>
          </select>
        </div>
        <p className="text-xs text-slate-400">El token se cifra antes de guardarse.</p>
      </Modal>
    </div>
  );
}
